const COMMANDS = Object.freeze({
  alpha: "α", beta: "β", gamma: "γ", delta: "δ", epsilon: "ε", varepsilon: "ϵ",
  zeta: "ζ", eta: "η", theta: "θ", vartheta: "ϑ", iota: "ι", kappa: "κ",
  lambda: "λ", mu: "μ", nu: "ν", xi: "ξ", omicron: "ο", pi: "π", varpi: "ϖ",
  rho: "ρ", varrho: "ϱ", sigma: "σ", varsigma: "ς", tau: "τ", upsilon: "υ",
  phi: "φ", varphi: "ϕ", chi: "χ", psi: "ψ", omega: "ω",
  Gamma: "Γ", Delta: "Δ", Theta: "Θ", Lambda: "Λ", Xi: "Ξ", Pi: "Π",
  Sigma: "Σ", Upsilon: "Υ", Phi: "Φ", Psi: "Ψ", Omega: "Ω",
  pm: "±", mp: "∓", times: "×", div: "÷", cdot: "·", ast: "∗", star: "⋆",
  circ: "∘", bullet: "∙", cap: "∩", cup: "∪", uplus: "⊎", sqcap: "⊓",
  sqcup: "⊔", vee: "∨", wedge: "∧", setminus: "∖", wr: "≀",
  le: "≤", leq: "≤", ge: "≥", geq: "≥", ne: "≠", neq: "≠", equiv: "≡",
  approx: "≈", sim: "∼", simeq: "≃", cong: "≅", propto: "∝", ll: "≪", gg: "≫",
  in: "∈", notin: "∉", ni: "∋", subset: "⊂", supset: "⊃", subseteq: "⊆",
  supseteq: "⊇", parallel: "∥", perp: "⊥", mid: "∣", models: "⊨",
  to: "→", rightarrow: "→", leftarrow: "←", leftrightarrow: "↔",
  Rightarrow: "⇒", Leftarrow: "⇐", Leftrightarrow: "⇔", mapsto: "↦",
  uparrow: "↑", downarrow: "↓", updownarrow: "↕", longrightarrow: "⟶",
  longleftarrow: "⟵", longleftrightarrow: "⟷",
  sum: "∑", prod: "∏", coprod: "∐", int: "∫", iint: "∬", iiint: "∭",
  oint: "∮", partial: "∂", nabla: "∇", infty: "∞", ell: "ℓ", hbar: "ℏ",
  Re: "ℜ", Im: "ℑ", wp: "℘", aleph: "ℵ", emptyset: "∅", varnothing: "∅",
  forall: "∀", exists: "∃", nexists: "∄", neg: "¬", land: "∧", lor: "∨",
  therefore: "∴", because: "∵", angle: "∠", measuredangle: "∡",
  ldots: "…", dots: "…", cdots: "⋯", vdots: "⋮", ddots: "⋱",
  prime: "′", degree: "°", triangle: "△", square: "□", diamond: "◇",
  mathbbR: "ℝ", mathbbN: "ℕ", mathbbZ: "ℤ", mathbbQ: "ℚ", mathbbC: "ℂ"
});

const FUNCTION_NAMES = new Set([
  "sin", "cos", "tan", "cot", "sec", "csc", "arcsin", "arccos", "arctan",
  "sinh", "cosh", "tanh", "log", "ln", "exp", "lim", "min", "max", "inf",
  "sup", "det", "dim", "gcd", "ker", "hom", "arg", "Pr"
]);

const STYLE_COMMANDS = new Set([
  "mathrm", "mathbf", "mathit", "mathsf", "mathtt", "mathcal", "mathscr",
  "mathfrak", "boldsymbol", "bm", "text", "textrm", "textbf",
  "textit", "operatorname"
]);

const IGNORED_COMMANDS = new Set([
  "left", "right", "middle", "limits", "nolimits", "displaystyle", "textstyle",
  "scriptstyle", "scriptscriptstyle", "!", ",", ":", ";", "quad", "qquad"
]);

const SUPERSCRIPTS = Object.freeze({
  "0": "⁰", "1": "¹", "2": "²", "3": "³", "4": "⁴", "5": "⁵", "6": "⁶",
  "7": "⁷", "8": "⁸", "9": "⁹", "+": "⁺", "-": "⁻", "−": "⁻", "=": "⁼",
  "(": "⁽", ")": "⁾", n: "ⁿ", i: "ⁱ", x: "ˣ", y: "ʸ"
});

const SUBSCRIPTS = Object.freeze({
  "0": "₀", "1": "₁", "2": "₂", "3": "₃", "4": "₄", "5": "₅", "6": "₆",
  "7": "₇", "8": "₈", "9": "₉", "+": "₊", "-": "₋", "−": "₋", "=": "₌",
  "(": "₍", ")": "₎", a: "ₐ", e: "ₑ", h: "ₕ", i: "ᵢ", j: "ⱼ", k: "ₖ",
  l: "ₗ", m: "ₘ", n: "ₙ", o: "ₒ", p: "ₚ", r: "ᵣ", s: "ₛ", t: "ₜ",
  u: "ᵤ", v: "ᵥ", x: "ₓ"
});

export function latexToReadableMath(source) {
  const parser = new LatexTextParser(String(source ?? ""));
  const text = parser.parse()
    .replace(/[ \t]*\n[ \t]*/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
  return { text, unsupportedCommands: [...parser.unsupportedCommands] };
}

class LatexTextParser {
  constructor(source) {
    this.source = source.replace(/\r\n?/g, "\n");
    this.index = 0;
    this.unsupportedCommands = new Set();
  }

  parse(stop = "") {
    let output = "";
    while (this.index < this.source.length) {
      const character = this.source[this.index];
      if (stop && character === stop) {
        this.index += 1;
        break;
      }
      if (character === "\\") {
        output += this.readCommand();
        continue;
      }
      if (character === "{") {
        this.index += 1;
        output += this.parse("}");
        continue;
      }
      if (character === "}") {
        this.index += 1;
        if (stop) break;
        output += "}";
        continue;
      }
      if (character === "^" || character === "_") {
        this.index += 1;
        const argument = this.readArgument();
        output += scriptText(argument, character === "^" ? SUPERSCRIPTS : SUBSCRIPTS, character);
        continue;
      }
      if (character === "&") {
        this.index += 1;
        output += "  ";
        continue;
      }
      if (character === "~") {
        this.index += 1;
        output += " ";
        continue;
      }
      output += character === "-" ? "−" : character;
      this.index += 1;
    }
    return output;
  }

  readCommand() {
    this.index += 1;
    if (this.source[this.index] === "\\") {
      this.index += 1;
      return "\n";
    }
    const escaped = this.source[this.index];
    if (escaped && /[$%#&_{}]/.test(escaped)) {
      this.index += 1;
      return escaped;
    }
    const match = /^[A-Za-z]+/.exec(this.source.slice(this.index));
    const command = match?.[0] || this.source[this.index] || "";
    this.index += command.length;

    if (COMMANDS[command]) return COMMANDS[command];
    if (FUNCTION_NAMES.has(command)) return command;
    if (IGNORED_COMMANDS.has(command)) return command === "quad" || command === "qquad" ? " " : "";
    if (STYLE_COMMANDS.has(command)) return this.readArgument();
    if (command === "frac" || command === "dfrac" || command === "tfrac") {
      const numerator = this.readArgument();
      const denominator = this.readArgument();
      return `${fractionPart(numerator)}⁄${fractionPart(denominator)}`;
    }
    if (command === "binom" || command === "dbinom" || command === "tbinom") {
      return `(${this.readArgument()}¦${this.readArgument()})`;
    }
    if (command === "sqrt") {
      const degree = this.readOptionalArgument();
      const radicand = this.readArgument();
      const symbol = degree === "3" ? "∛" : degree === "4" ? "∜" : "√";
      return `${symbol}${degree && degree !== "3" && degree !== "4" ? `[${degree}]` : ""}(${radicand})`;
    }
    if (command === "overline" || command === "bar") return combining(this.readArgument(), "\u0305");
    if (command === "underline") return combining(this.readArgument(), "\u0332");
    if (command === "hat" || command === "widehat") return combining(this.readArgument(), "\u0302");
    if (command === "tilde" || command === "widetilde") return combining(this.readArgument(), "\u0303");
    if (command === "dot") return combining(this.readArgument(), "\u0307");
    if (command === "ddot") return combining(this.readArgument(), "\u0308");
    if (command === "vec" || command === "overrightarrow") return combining(this.readArgument(), "\u20D7");
    if (command === "overleftarrow") return combining(this.readArgument(), "\u20D6");
    if (command === "overset" || command === "stackrel") {
      const top = this.readArgument();
      const base = this.readArgument();
      return `${base}${scriptText(top, SUPERSCRIPTS, "^")}`;
    }
    if (command === "underset") {
      const bottom = this.readArgument();
      const base = this.readArgument();
      return `${base}${scriptText(bottom, SUBSCRIPTS, "_")}`;
    }
    if (command === "begin" || command === "end") {
      this.readArgument();
      return command === "end" ? "" : "\n";
    }
    if (command === "color" || command === "textcolor") {
      this.readArgument();
      return this.readArgument();
    }
    if (command === "mathbb") {
      const value = this.readArgument();
      return COMMANDS[`mathbb${value}`] || value;
    }
    if (!command) return "\\";
    this.unsupportedCommands.add(`\\${command}`);
    return `\\${command}`;
  }

  readArgument() {
    this.skipSpaces();
    if (this.source[this.index] === "{") {
      this.index += 1;
      return this.parse("}");
    }
    if (this.source[this.index] === "\\") return this.readCommand();
    const character = this.source[this.index] || "";
    this.index += character ? 1 : 0;
    return character;
  }

  readOptionalArgument() {
    this.skipSpaces();
    if (this.source[this.index] !== "[") return "";
    this.index += 1;
    const end = this.source.indexOf("]", this.index);
    if (end < 0) return "";
    const value = this.source.slice(this.index, end);
    this.index = end + 1;
    return value;
  }

  skipSpaces() {
    while (/[ \t\n]/.test(this.source[this.index] || "")) this.index += 1;
  }
}

function scriptText(value, alphabet, fallback) {
  const normalized = String(value).replace(/\s+/g, "");
  const existing = new Set(Object.values(alphabet));
  if (normalized && [...normalized].every(character => alphabet[character] || existing.has(character))) {
    return [...normalized].map(character => alphabet[character] || character).join("");
  }
  return `${fallback}(${value})`;
}

function fractionPart(value) {
  const text = String(value).trim();
  return /^[\p{L}\p{N}]+$/u.test(text) ? text : `(${text})`;
}

function combining(value, mark) {
  return [...String(value)].map(character => /\s/u.test(character) ? character : `${character}${mark}`).join("");
}
